<template>
    <FormSidebar
        :id="id"
        :key="id"
        v-model:form="content"
        :title="sidebar?.title"
        :action-button="sidebar?.button"
        :action="sidebar?.action"
        :pre-action="sidebar?.preAction"
        :validatable="sidebar?.validatable"
        :save-response-type="actionResponseType"
        :update-action="sidebar?.update"
        :pinnable="sidebar?.pinnable"
        :is-shown="show"
        @save="onSave"
        @error="onError"
        @hide="hide"
        @hidden="onHidden"
    />
</template>

<script lang="ts" setup>
import {
    computed, ref, watch,
} from 'vue';
import { useRouter } from 'vue-router';
import {
    EventType,
    useSidebarStore,
    type Event,
    type ResponseEvent,
} from '@/vue/stores/sidebar';
import type { Form } from '../../../scripts/helpers/schemaConverter/converters/types/Form';
import FormSidebar from './FormSidebar.vue';
import { useSidebarSocketLogic } from './useSidebarSocketLogic';

const router = useRouter();
const sidebarStore = useSidebarStore();

const sidebar = computed(() => sidebarStore.sidebar);
const hasSidebar = computed(() => sidebarStore.hasSidebar);
const actionResponseType = computed(() => sidebarStore.actionResponseType);

const createTarget = () => ({
    $router: router,
});

const show = ref(hasSidebar.value);

const hide = (): void => {
    show.value = false;
};

const onHidden = () => {
    const event: Event = { type: EventType.CANCEL, target: createTarget() };
    sidebarStore.dispatch(event);
    sidebarStore.clearSidebar();
};

const onSave = (response: unknown): void => {
    const event: ResponseEvent = { type: EventType.SAVE, target: createTarget(), response };
    sidebarStore.dispatch(event);
};

const onError = (
    { response, handledRef }: { response: unknown, handledRef: { value: boolean } },
): void => {
    const event: ResponseEvent = { type: EventType.ERROR, target: createTarget(), response };
    sidebarStore.dispatch(event);
    handledRef.value = event.handled === true;
};

const content = computed<Form|undefined>({
    get: () => sidebar.value?.content,
    set: (value) => {
        if (value === undefined) {
            hide();
            return;
        }

        sidebarStore.setContent(value);
    },
});

const id = computed<string>(
    () => (content.value ? `sidebar_${content.value.name}` : 'sidebar_empty'),
);

watch(hasSidebar, () => {
    show.value = hasSidebar.value;
});

useSidebarSocketLogic({
    hideSidebar: hide,
});

</script>
